/**
 * THE PRODUCT-BUILDERS → BUILDER HAND-OFF. Pure, no React, no DOM.
 *
 * ⚠️ THE DEAD BUTTON THIS FIXES. `pages/ProductBuilders.jsx` ships a
 * "Generate concept · Artwork Studio" link on every product, pointing at
 * `/artwork-studio?flow=<id>`. That parameter was only ever read by
 * `ConceptGenerator.jsx#initialFlow()` — which was retired and unrouted months
 * ago. So the button has been landing reps on the Builder with the flow
 * silently discarded: not an error, not a 404, just nothing. A live page with a
 * dead control is worse than no control, because the rep concludes the feature
 * is broken rather than absent.
 *
 * ⚠️ AND IT IS NOT MAPPED BY GUESSING. A flow with no honest destination
 * returns `null` and `ProductBuilders` renders no link for it at all, with the
 * reason. Two of the four flows are in that state today:
 *
 *   coin      the Builder has no `coin` product — the products list is
 *             ring / pendant / lapel-pin / stick-on-lapel / cufflinks /
 *             bracelet / belt-buckle, and only `ring` has a beast.
 *   sr-grad   the class ring is a real product, but the Builder's style
 *             catalog is championship / band / signet / letterwinner /
 *             corporate. None of those IS a class ring. Seeding `signet`
 *             because it is the nearest word would start the operator on the
 *             wrong style and look deliberate.
 *
 * The seed is applied ONCE, before the operator has touched anything, and never
 * over a choice already made — `Studio.jsx` guards that with `seeded`.
 */

/**
 * flow id → what it seeds in the Builder. `null` means "no honest destination
 * yet"; the caller must not render a link.
 */
export const FLOW_SEEDS = {
  'champ-ring': { product: 'ring', style: 'championship' },
  'champ-pendant': { product: 'pendant', style: 'championship' },
  'sr-grad': null,
  coin: null,
}

/** Why a flow has no destination, for the page that would have linked to it. */
export const FLOW_BLOCKED = {
  'sr-grad': 'The Builder has no class-ring style yet — championship, band, signet, letterwinner and corporate are the five, and none of them is a class ring.',
  coin: 'The Builder has no challenge-coin product yet.',
}

/** Does this flow have somewhere real to land? */
export function flowSupported(flow) {
  return !!FLOW_SEEDS[String(flow || '')]
}

/** Why not, in words a rep can act on. '' when it is supported. */
export function flowBlockedReason(flow) {
  const f = String(flow || '')
  if (flowSupported(f)) return ''
  return FLOW_BLOCKED[f] || 'This product has no Builder flow yet.'
}

/**
 * Read `?flow=` off a query string and return what it seeds.
 * Always returns an object, so the caller can spread it unconditionally; an
 * unknown or unmapped flow seeds NOTHING rather than a default that would
 * quietly override the Builder's own.
 *
 * @param {string} search  `window.location.search`
 * @returns {{product?: string, style?: string}}
 */
export function flowSeed(search) {
  let flow = ''
  try {
    flow = new URLSearchParams(String(search || '')).get('flow') || ''
  } catch {
    return {}
  }
  const seed = FLOW_SEEDS[flow]
  return seed ? { ...seed } : {}
}
